import { Spacing } from '@persona-id/design-tokens';
import { ArrowCollapseIcon, ArrowExpandIcon } from '@persona-id/design-tokens/icons';
import Highlight, { defaultProps, Language, PrismTheme } from 'prism-react-renderer';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Prism from 'prism-react-renderer/prism';
import nightOwlLight from 'prism-react-renderer/themes/nightOwlLight';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Button from 'design-system/components/Button';
import useToggle from 'lib/hooks/useToggle';

const LINE_HEIGHT = 1.5;

export const Wrapper = styled.div`
  font-family: ${({ theme }) => theme.text.fontFamily.mono};
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  line-height: ${LINE_HEIGHT};
  text-align: center;
  position: relative;
`;

export const ExpandButtonContainer = styled.div`
  position: absolute;
  top: ${Spacing[2]};
  right: ${Spacing[2]};
`;

export const Pre = styled.pre<{ expanded: boolean; rowsToShow?: number }>`
  max-height: ${({ theme, expanded, rowsToShow }) => {
    if (expanded || rowsToShow == null) {
      return 'unset';
    }

    const charHeight = parseFloat(theme.text.paragraph.sm.fontSize);
    const rowHeight = charHeight * LINE_HEIGHT;
    const marginHeight = charHeight * 2; // margin is 1em
    // Row height + top/bottom padding
    return `${rowsToShow * rowHeight + marginHeight}px`;
  }};

  border-radius: ${({ theme }) => theme.element.borderRadius};
  font-family: inherit;
  text-align: left;
  padding: 1em;
  overflow: auto;

  a {
    /* overwrite dashboard global styles */
    font-size: inherit;
    line-height: inherit;
    text-decoration: underline;
  }
`;

export const Line = styled.div`
  display: table-row;
`;

export const LineNo = styled.span`
  display: table-cell;
  text-align: right;
  padding-right: 1em;
  user-select: none;
  opacity: 0.5;
`;

export const LineContent = styled.span`
  display: table-cell;
`;

// Provided by default by react-prism-highlight
// node_modules/prism-react-renderer/index.d.ts
const DEFAULT_LANGUAGES = new Set([
  'markup',
  'bash',
  'clike',
  'c',
  'cpp',
  'css',
  'javascript',
  'jsx',
  'coffeescript',
  'actionscript',
  'css-extr',
  'diff',
  'git',
  'go',
  'graphql',
  'handlebars',
  'json',
  'less',
  'makefile',
  'markdown',
  'objectivec',
  'ocaml',
  'python',
  'reason',
  'sass',
  'scss',
  'sql',
  'stylus',
  'tsx',
  'typescript',
  'wasm',
  'yaml',
]);

async function loadPrism(language: CodeHighlightProps['language']) {
  (window as any).Prism = Prism;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (!DEFAULT_LANGUAGES.has(language)) {
    await import(`prismjs/components/prism-${language}`);
  }
}

// mirrored from lib -- not exported
type Token = {
  types: string[];
  content: string;
  empty?: boolean;
};

const SLUG_TO_ROUTE: Record<string, string> = {
  act: `/dashboard/accounts`,
  api: `/dashboard/api-keys`,
  case: `/dashboard/cases`,
  ctmpl: `/dashboard/case-templates`,
  eil: `/dashboard/integration-logs`,
  evt: `/dashboard/events`,
  inq: `/dashboard/inquiries`,
  // TODO(PER-17893) Allow for linking to Inquiry Template without the version
  // itmpl: `/dashboard/inquiry-templates`,
  lst: `/dashboard/lists`,
  rep: `/dashboard/reports`,
  req: `/dashboard/api-logs`,
  rptp: `/dashboard/report-templates`,
  rss: `/dashboard/report-snapshot`,
  rtsk: `/dashboard/queues/tasks`,
  tmpl: `/dashboard/inquiry-templates`,
  txn: `/dashboard/transactions`,
  txntp: `/dashboard/transaction-types`,
  ver: `/dashboard/verifications`,
  wbh: `/dashboard/webhooks`,
  wbhe: `/dashboard/webhook-events`,
  wfl: `/dashboard/workflows`,
};

const linkToEntity = ({ types, content }: Token) => {
  if (types[0] !== 'string') {
    return;
  }
  // strip the quotes
  const token = content.slice(1, content.length - 1);
  const [prefix, id, shouldNotExist] = token.split('_');
  // A token should only have a prefix + id
  if (prefix == null || id == null || shouldNotExist != null) {
    return;
  }
  // See app/lib/prefixed_token.rb for possible lengths
  if (!(id.length === 28 || id.length === 24)) {
    return;
  }
  if (SLUG_TO_ROUTE[prefix] == null) {
    return;
  }

  return `${SLUG_TO_ROUTE[prefix]}/${token}`;
};

interface CodeHighlightProps {
  id?: string;
  children: string;
  language?: Language;
  rowsToShow?: number;
  light?: boolean;
  className?: string;
}

const CodeHighlight = ({
  id,
  children = '',
  language = 'markup',
  rowsToShow,
  light = false,
  className,
}: CodeHighlightProps) => {
  useEffect(() => {
    loadPrism(language);
  }, [language, children]);

  const [expanded, { toggle: toggleExpanded }] = useToggle();
  const rowCount = useMemo(() => children.match(/\n/g)?.length ?? 0, [children]);

  const allowExpanding = rowsToShow != null && rowsToShow < rowCount;

  return (
    <Wrapper className={className}>
      {allowExpanding && (
        <ExpandButtonContainer>
          <Button
            width='icon'
            onClick={toggleExpanded}
            variant='inverse'
            data-test='code-highlight__expand'
          >
            {expanded ? <ArrowCollapseIcon /> : <ArrowExpandIcon />}
          </Button>
        </ExpandButtonContainer>
      )}
      <Highlight
        {...defaultProps}
        data-test='code-highlight'
        theme={light ? nightOwlLight : THEME}
        code={children}
        language={language}
      >
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <Pre
            className={className}
            style={style}
            data-test={id}
            expanded={expanded}
            rowsToShow={rowsToShow}
          >
            {tokens.map((line, i) => (
              <Line key={i} {...getLineProps({ line, key: i })}>
                <LineNo>{i + 1}</LineNo>
                <LineContent>
                  {line.map((token, key) => {
                    const tokenProps = getTokenProps({ token, key });
                    const tokenLink = linkToEntity(token);

                    return tokenLink != null ? (
                      <span {...tokenProps}>
                        &quot;
                        <Link to={tokenLink} {...tokenProps}>
                          {tokenProps.children.slice(1, tokenProps.children.length - 1)}
                        </Link>
                        &quot;
                      </span>
                    ) : (
                      <span key={key} {...tokenProps} />
                    );
                  })}
                </LineContent>
              </Line>
            ))}
          </Pre>
        )}
      </Highlight>
    </Wrapper>
  );
};

export default CodeHighlight;

// Based off of https://github.com/FormidableLabs/prism-react-renderer/blob/master/src/themes/duotoneDark.js
const THEME: PrismTheme = {
  plain: {
    backgroundColor: '#161320', // edited
    color: '#9a86fd',
  },
  styles: [
    {
      types: ['comment', 'prolog', 'doctype', 'cdata', 'punctuation'],
      style: {
        color: '#6c6783',
      },
    },
    {
      types: ['namespace'],
      style: {
        opacity: 0.7,
      },
    },
    {
      types: ['tag', 'operator', 'number', 'keyword'],
      style: {
        color: '#e09142',
        fontWeight: 'bold', // added for more contrast
      },
    },
    {
      types: ['property', 'function'],
      style: {
        color: '#9a86fd',
      },
    },
    {
      types: ['tag-id', 'selector', 'atrule-id'],
      style: {
        color: '#eeebff',
      },
    },
    {
      types: ['attr-name'],
      style: {
        color: '#c4b9fe',
      },
    },
    {
      types: [
        'boolean',
        'string',
        'entity',
        'url',
        'attr-value',
        'control',
        'directive',
        'unit',
        'statement',
        'regex',
        'at-rule',
        'placeholder',
        'variable',
      ],
      style: {
        color: '#ffcc99',
      },
    },
    {
      types: ['deleted'],
      style: {
        textDecorationLine: 'line-through',
      },
    },
    {
      types: ['inserted'],
      style: {
        textDecorationLine: 'underline',
      },
    },
    {
      types: ['italic'],
      style: {
        fontStyle: 'italic',
      },
    },
    {
      types: ['important', 'bold'],
      style: {
        fontWeight: 'bold',
      },
    },
    {
      types: ['important'],
      style: {
        color: '#c4b9fe',
      },
    },
  ],
};
